import { marked } from 'marked';
import { type MouseEvent, useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { isSignedIn } from '../auth';
import { Logo } from '../components/Logo';
import { SetupDiagram } from '../components/SetupDiagram';
import { DOCS } from '../docs';
import { AMAZON_DISCLOSURE, AMAZON_TAG, amazonSearchUrl } from '../docs/affiliate';
import { CONTACT_EMAIL } from '../docs/contact';

const DIAGRAM = /<!--\s*diagram:finish\s*-->/;

/** Fill in the contact address, or fall back to plain words when none is set. */
function withContact(md: string): string {
  if (CONTACT_EMAIL) return md.replaceAll('{{contact}}', `[${CONTACT_EMAIL}](mailto:${CONTACT_EMAIL})`).replaceAll('{{contact-link}}', `mailto:${CONTACT_EMAIL}`);
  // "[Get in touch]({{contact-link}})" → "Get in touch"; "{{contact}}" → "the Splitline team".
  return md.replace(/\[([^\]]+)\]\(\{\{contact-link\}\}\)/g, '$1').replaceAll('{{contact}}', 'the Splitline team');
}

const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/**
 * "{{amazon:search words|Label}}" → a shopping link. With an Associates tag
 * set, links are marked sponsored and Amazon's disclosure goes under the title.
 */
function withShopping(md: string): string {
  const AMAZON = /\{\{amazon:([^|}]+)\|([^}]+)\}\}/g;
  if (!AMAZON.test(md)) return md;
  const linked = md.replace(AMAZON, (_, query: string, label: string) => {
    const rel = AMAZON_TAG ? 'sponsored noopener' : 'noopener';
    return `<a class="buy-link" href="${esc(amazonSearchUrl(query.trim()))}" target="_blank" rel="${rel}">${esc(label.trim())} on Amazon ↗</a>`;
  });
  return AMAZON_TAG ? linked.replace(/^(# .*\n)/, `$1\n<p class="disclosure">${esc(AMAZON_DISCLOSURE)}</p>\n`) : linked;
}

/** The customer guide: Markdown pages from src/docs with a sidebar. Public. */
export function DocsPage() {
  const { slug = DOCS[0]!.slug } = useParams();
  const navigate = useNavigate();
  const index = DOCS.findIndex((d) => d.slug === slug);
  const doc = DOCS[index];

  // Our own Markdown, rendered once per page; the diagram placeholder splits it.
  const parts = useMemo(() => (doc ? withShopping(withContact(doc.body)).split(DIAGRAM).map((md) => marked.parse(md, { async: false, gfm: true }) as string) : []), [doc]);

  useEffect(() => {
    if (doc) document.title = `${doc.title} · Splitline guide`;
    window.scrollTo(0, 0);
  }, [doc]);

  if (!doc) return <Navigate to="/docs" replace />;

  // Links inside the Markdown navigate in-app instead of reloading.
  const onClick = (e: MouseEvent) => {
    const a = (e.target as HTMLElement).closest('a');
    const href = a?.getAttribute('href');
    if (!a || !href || !href.startsWith('/') || a.hasAttribute('download') || href.endsWith('.csv') || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    navigate(href);
  };

  const sections = [...new Set(DOCS.map((d) => d.section))];
  const prev = DOCS[index - 1];
  const next = DOCS[index + 1];

  return (
    <>
      <header className="bar">
        <div className="bar-inner">
          <Link to="/" className="brand">
            <Logo />
            <span>Splitline</span>
          </Link>
          <nav aria-label="Primary" className="nav">
            <Link to="/docs" aria-current="page">
              Guide
            </Link>
          </nav>
          <div className="bar-right">
            {isSignedIn() ? (
              <Link to="/" style={{ color: 'var(--bar-muted)' }}>
                Your races
              </Link>
            ) : (
              <>
                <Link to="/login" style={{ color: 'var(--bar-muted)' }}>
                  Sign in
                </Link>
                <Link to="/signup" className="btn primary small">
                  Sign up free
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="page docs-layout">
        <nav className="docs-nav" aria-label="Guide">
          {sections.map((section) => (
            <div key={section} className="stack" style={{ gap: 4 }}>
              <div className="docs-nav-section">{section}</div>
              {DOCS.filter((d) => d.section === section).map((d) => (
                <Link key={d.slug} to={`/docs/${d.slug}`} className={d.slug === doc.slug ? 'active' : undefined} aria-current={d.slug === doc.slug ? 'page' : undefined}>
                  {d.title}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <article className="docs-body" onClick={onClick}>
          {parts.map((html, i) => (
            <div key={i}>
              {i > 0 && (
                <figure className="docs-figure">
                  <SetupDiagram />
                </figure>
              )}
              <div dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          ))}
          <div className="docs-pager">
            {prev ? <Link to={`/docs/${prev.slug}`}>← {prev.title}</Link> : <span />}
            {next ? <Link to={`/docs/${next.slug}`}>{next.title} →</Link> : <span />}
          </div>
        </article>
      </main>
    </>
  );
}
